import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ArrowUpRight,
  BadgeCheck,
  BookOpenCheck,
  BriefcaseBusiness,
  Check,
  ChevronDown,
  CircleDollarSign,
  ClipboardCheck,
  Grid2x2Check,
  Laptop,
  Luggage,
  SearchCheck,
  Ruler,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { criteria, Product, products } from "./data/products";
import { getDestinationHost, trackEvent } from "./lib/analytics";
import { installAnalyticsScripts } from "./lib/analyticsScripts";

const criteriaIcons = [Ruler, Laptop, Grid2x2Check, BriefcaseBusiness, Sparkles, CircleDollarSign];
const lastUpdated = "July 13, 2026";
const homePath = "/work-totes";
const siteHomePath = "/";
const guidesPath = "/guides";
const buyingGuidePath = "/guides/work-tote-buying-guide";
const commuteChecklistPath = "/guides/work-tote-commute-checklist";
const sizeGuidePath = "/guides/work-tote-size-guide";
const materialsGuidePath = "/guides/leather-vs-nylon-work-totes";
const howWePickPath = "/how-we-pick-work-totes";

const faqs = [
  {
    question: "What should I look for in a work tote?",
    answer:
      "Look for structure, a comfortable shoulder drop, laptop-friendly capacity, useful pockets, and a closure that fits your commute.",
  },
  {
    question: "Do these totes fit laptops?",
    answer:
      "Many picks are selected for laptop-friendly carry, but exact fit depends on your laptop size and the merchant's product details. Check the merchant page before buying.",
  },
  {
    question: "Are these designer dupes?",
    answer:
      "No. We avoid replica and counterfeit framing. This guide focuses on transferable style needs: polished shape, low-logo design, office readiness, and practical organization.",
  },
  {
    question: "Why do some links go to other stores?",
    answer:
      "We are an independent shopping guide. Product buttons open merchant sites where you can review final details, shipping, returns, and checkout.",
  },
];

function destinationUrl(product: Product) {
  return product.affiliateUrl || product.merchantUrl;
}

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function trackHeaderLink(target: string, destinationPath: string) {
  trackEvent("nav_click", {
    target,
    destination_path: destinationPath,
    nav_location: "header",
  });
}

function trackFooterLink(target: string, destinationPath: string) {
  trackEvent("footer_link_click", {
    target,
    destination_path: destinationPath,
    nav_location: "footer",
  });
}

function trackEditorialCta(sourcePage: string, ctaId: string, target: string, destinationPath: string) {
  trackEvent("editorial_cta_click", {
    source_page: sourcePage,
    cta_id: ctaId,
    target,
    destination_path: destinationPath,
  });
}

function trackMailto(sourcePage: string, emailRole: string, emailDomain: string) {
  trackEvent("mailto_click", {
    source_page: sourcePage,
    email_role: emailRole,
    email_domain: emailDomain,
  });
}

function AppHeader() {
  return (
    <header className="topbar">
      <nav className="nav shell" aria-label="Primary navigation">
        <a className="brand" href={siteHomePath} onClick={() => trackHeaderLink("site_home", siteHomePath)}>
          Office Carry Guide
        </a>
        <div className="nav-links">
          <a href={`${homePath}#picks`} onClick={() => trackHeaderLink("picks", `${homePath}#picks`)}>
            Picks
          </a>
          <a
            className="desktop-nav-link"
            href={`${homePath}#compare`}
            onClick={() => trackHeaderLink("compare", `${homePath}#compare`)}
          >
            Compare
          </a>
          <a
            className="desktop-nav-link"
            href={howWePickPath}
            onClick={() => trackHeaderLink("how_we_pick", howWePickPath)}
          >
            How we pick
          </a>
          <a href={guidesPath} onClick={() => trackHeaderLink("guides", guidesPath)}>
            Guides
          </a>
          <a href="/about" onClick={() => trackHeaderLink("about", "/about")}>
            About
          </a>
        </div>
      </nav>
    </header>
  );
}

function Hero() {
  const onCtaClick = () => {
    trackEvent("hero_cta_click", { target: "picks" });
  };

  return (
    <section className="hero shell" id="top">
      <div className="hero-copy">
        <p className="eyebrow">Office Carry Edit · Updated July 2026</p>
        <h1>Polished Work Totes That Look Expensive</h1>
        <p className="hero-subtitle">
          Laptop-friendly, office-ready totes selected for structure, organization, and quiet style across practical
          price points.
        </p>
        <div className="hero-actions">
          <a className="button button-primary" href="#picks" onClick={onCtaClick}>
            View the picks
          </a>
          <a className="button button-secondary" href="#compare">
            Compare details
          </a>
        </div>
        <div className="trust-row" aria-label="Trust signals">
          <span>
            <ShieldCheck size={16} /> No logo-chasing
          </span>
          <span>
            <Laptop size={16} /> Laptop details checked
          </span>
          <span>
            <BadgeCheck size={16} /> Independent guide
          </span>
        </div>
        <p className="hero-disclosure">Some links may earn us a commission, at no extra cost to you.</p>
      </div>
      <picture className="hero-media">
        <source media="(max-width: 720px)" srcSet="/images/hero-mobile.webp" />
        <img src="/images/hero-desktop.webp" alt="Structured black work tote on a clean office desk" />
      </picture>
    </section>
  );
}

function CriteriaSection() {
  return (
    <section className="criteria-band" aria-labelledby="criteria-title">
      <div className="shell">
        <div className="section-heading">
          <p className="eyebrow">Professional selection criteria</p>
          <h2 id="criteria-title">What makes a work tote worth carrying?</h2>
        </div>
        <div className="criteria-grid">
          {criteria.map((item, index) => {
            const Icon = criteriaIcons[index];
            return (
              <button
                className="criteria-item"
                key={item.id}
                type="button"
                onClick={() => trackEvent("criteria_click", { criteria_id: item.id, criteria_label: item.label })}
              >
                <span className="criteria-icon" aria-hidden="true">
                  <Icon size={20} />
                </span>
                <span className="criteria-label">{item.label}</span>
                <span className="criteria-description">{item.description}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProductCard({ product }: { product: Product }) {
  const url = destinationUrl(product);
  const host = getDestinationHost(url);

  const onMerchantClick = () => {
    trackEvent("affiliate_outbound_click", {
      product_id: product.id,
      product_name: product.productName,
      merchant: product.trackingMerchant,
      price_tier: product.priceTier,
      position: product.rank,
      badge: product.badge,
      destination_url_host: host,
    });
  };

  return (
    <article className="product-card" id={product.id} data-product-id={product.id}>
      <div className="product-image-wrap">
        <img className="product-image" src={product.imageUrl} alt={product.imageAlt} />
        <span className="rank-badge">#{product.rank}</span>
        <span className="product-badge">{product.badge}</span>
      </div>
      <div className="product-body">
        <div className="product-title-row">
          <div>
            <h3>{product.productName}</h3>
            <p>{product.merchant}</p>
          </div>
          <strong>{product.price}</strong>
        </div>
        <p className="best-for">Best for: {product.bestFor}</p>
        <div className="detail-chip-row">
          <span>
            <Laptop size={14} /> {product.laptop}
          </span>
          <span>
            <Luggage size={14} /> {product.travelDetail}
          </span>
        </div>
        <ul className="feature-list">
          {product.features.map((feature) => (
            <li key={feature}>
              <Check size={15} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <div className="mini-detail-grid" aria-label={`${product.productName} detail images`}>
          {product.detailImages.map((image) => (
            <img key={image} src={image} alt="" loading="lazy" />
          ))}
        </div>
        <p className="editor-note">{product.editorNote}</p>
        <a
          className="merchant-link"
          href={url}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={onMerchantClick}
          data-umami-event="affiliate_outbound_click"
          data-umami-event-theme="quiet_luxury_work_totes"
          data-umami-event-merchant={product.trackingMerchant}
          data-umami-event-product={product.trackingProduct}
          data-umami-event-position={product.rank}
        >
          View at merchant <ArrowUpRight size={16} />
        </a>
        <span className="external-note">Opens merchant site</span>
      </div>
    </article>
  );
}

function ProductSection() {
  return (
    <section className="picks shell" id="picks" aria-labelledby="picks-title">
      <div className="section-heading split-heading">
        <div>
          <p className="eyebrow">Featured picks</p>
          <h2 id="picks-title">The Work Tote Edit</h2>
        </div>
        <p>Six polished picks for office days, commutes, and laptop carry.</p>
      </div>
      <div className="product-grid">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </section>
  );
}

function CompareSection() {
  const onCompareClick = (product: Product) => {
    trackEvent("comparison_product_click", {
      product_id: product.id,
      product_name: product.productName,
      merchant: product.trackingMerchant,
      position: product.rank,
    });
    scrollToId(product.id);
  };

  return (
    <section className="compare-band" id="compare" aria-labelledby="compare-title">
      <div className="shell">
        <div className="section-heading split-heading">
          <div>
            <p className="eyebrow">Shopping decision module</p>
            <h2 id="compare-title">Compare the picks</h2>
          </div>
          <p>Use this strip to move from browsing to a short list.</p>
        </div>
        <div className="compare-scroller">
          <table className="compare-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Best for</th>
                <th>Price</th>
                <th>Laptop</th>
                <th>Closure</th>
                <th>Travel detail</th>
                <th>Merchant</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td>
                    <button type="button" onClick={() => onCompareClick(product)}>
                      {product.productName}
                    </button>
                  </td>
                  <td>{product.bestFor}</td>
                  <td>{product.price}</td>
                  <td>{product.laptop}</td>
                  <td>{product.closure}</td>
                  <td>{product.travelDetail}</td>
                  <td>{product.merchant}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function MethodSection() {
  return (
    <section className="method shell" id="how-we-picked" aria-labelledby="method-title">
      <div className="method-copy">
        <p className="eyebrow">How we picked</p>
        <h2 id="method-title">A work tote should make the day easier, not just look tidy.</h2>
        <p>
          We look for work totes with a polished, low-logo design, enough structure for office settings,
          laptop-friendly capacity, practical organization, and everyday commute comfort. We also include different
          price points so the list is not limited to one budget or one brand.
        </p>
        <p className="method-disclosure">
          Our picks are organized around use case, structure, and carry details rather than brand sponsorship or logo
          chasing.
        </p>
      </div>
      <div className="method-images">
        <img src="/images/detail-interior-pockets.webp" alt="Interior organization detail in a work tote" />
        <img src="/images/detail-shoulder-drop.webp" alt="Structured tote in an office setting with blazer nearby" />
      </div>
    </section>
  );
}

function FaqSection() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="faq shell" aria-labelledby="faq-title">
      <div className="section-heading">
        <p className="eyebrow">Before you click out</p>
        <h2 id="faq-title">Common work tote questions</h2>
      </div>
      <div className="faq-list">
        {faqs.map((item, index) => {
          const isOpen = index === openIndex;
          return (
            <div className="faq-item" key={item.question}>
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpenIndex(isOpen ? -1 : index)}
              >
                {item.question}
                <ChevronDown className={isOpen ? "rotate" : ""} size={18} />
              </button>
              {isOpen && <p>{item.answer}</p>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer" id="footer">
      <div className="shell footer-grid">
        <div>
          <a className="brand" href={siteHomePath} onClick={() => trackFooterLink("site_home", siteHomePath)}>
            Office Carry Guide
          </a>
          <p>
            Office Carry Guide is an independent shopping guide for polished, practical work totes. Some merchant links
            may earn us a commission at no extra cost to you.
          </p>
        </div>
        <div className="footer-links">
          <a href={howWePickPath} onClick={() => trackFooterLink("how_we_pick", howWePickPath)}>
            How We Pick
          </a>
          <a href={guidesPath} onClick={() => trackFooterLink("guides", guidesPath)}>
            Guides
          </a>
          <a href={buyingGuidePath} onClick={() => trackFooterLink("buying_guide", buyingGuidePath)}>
            Buying Guide
          </a>
          <a
            href={commuteChecklistPath}
            onClick={() => trackFooterLink("commute_checklist", commuteChecklistPath)}
          >
            Commute Checklist
          </a>
          <a href={sizeGuidePath} onClick={() => trackFooterLink("size_guide", sizeGuidePath)}>
            Size Guide
          </a>
          <a href={materialsGuidePath} onClick={() => trackFooterLink("materials_guide", materialsGuidePath)}>
            Materials Guide
          </a>
          <a href="/about" onClick={() => trackFooterLink("about", "/about")}>
            About
          </a>
          <a href="/contact" onClick={() => trackFooterLink("contact", "/contact")}>
            Contact
          </a>
          <a href="/affiliate-disclosure" onClick={() => trackFooterLink("affiliate_disclosure", "/affiliate-disclosure")}>
            Affiliate Disclosure
          </a>
          <a href="/privacy" onClick={() => trackFooterLink("privacy", "/privacy")}>
            Privacy Policy
          </a>
          <span>Updated July 2026</span>
        </div>
      </div>
    </footer>
  );
}

function HomePage() {
  return (
    <main>
      <section className="site-home-hero shell">
        <div className="site-home-copy">
          <p className="eyebrow">Office carry research</p>
          <h1>Practical buying guides for polished work carry.</h1>
          <p>
            Office Carry Guide helps readers choose bags and everyday carry pieces for office days, commutes, travel,
            and hybrid work. We start from real use cases first, then compare products, materials, organization, and
            merchant details.
          </p>
          <div className="hero-actions">
            <a
              className="button button-primary"
              href="/work-totes"
              onClick={() => trackEvent("home_cta_click", { target: "work_totes" })}
            >
              Explore work totes
            </a>
            <a
              className="button button-secondary"
              href={guidesPath}
              onClick={() => trackEvent("home_cta_click", { target: "guides" })}
            >
              Browse guides
            </a>
          </div>
        </div>
        <img
          className="site-home-image"
          src="/images/hero-desktop.webp"
          alt="Structured black work tote arranged in a clean office setting"
        />
      </section>

      <section className="site-home-section shell" aria-labelledby="current-guide-title">
        <div className="section-heading split-heading">
          <div>
            <p className="eyebrow">Current guide</p>
            <h2 id="current-guide-title">Work totes are our first vertical.</h2>
          </div>
          <p>
            The current project focuses on laptop-friendly, low-logo, office-ready totes. Future guides may expand into
            adjacent office carry needs when there is a clear consumer problem worth solving.
          </p>
        </div>
        <div className="site-home-card-grid">
          <a className="site-home-card" href="/work-totes">
            <span>01</span>
            <h3>Polished Work Totes</h3>
            <p>Compare current picks by structure, laptop fit, commute comfort, price, and merchant details.</p>
          </a>
          <a className="site-home-card" href={guidesPath}>
            <span>02</span>
            <h3>Guides Library</h3>
            <p>Read non-sales editorial guides on tote sizing, buying criteria, commute fit, and material tradeoffs.</p>
          </a>
          <a className="site-home-card" href={howWePickPath}>
            <span>03</span>
            <h3>How We Pick</h3>
            <p>See the editorial criteria behind the work tote shortlist and how we avoid dupe or replica framing.</p>
          </a>
        </div>
      </section>
    </main>
  );
}

function NotFoundPage() {
  return (
    <main className="not-found-page shell">
      <p className="eyebrow">Page not found</p>
      <h1>We could not find that Office Carry Guide page.</h1>
      <p>
        The link may be outdated, mistyped, or moved. Start with the current work tote guide or return to the site
        home.
      </p>
      <div className="hero-actions">
        <a className="button button-primary" href="/work-totes">
          View work tote picks
        </a>
        <a className="button button-secondary" href="/">
          Go to home
        </a>
      </div>
    </main>
  );
}

function EditorialHero({
  eyebrow,
  title,
  subtitle,
  image,
  imageAlt,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  image: string;
  imageAlt: string;
}) {
  return (
    <section className="editorial-hero shell">
      <div className="editorial-hero-copy">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      <img className="editorial-hero-image" src={image} alt={imageAlt} />
    </section>
  );
}

function EditorialCallout({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <aside className="editorial-callout">
      <span className="editorial-callout-icon" aria-hidden="true">
        {icon}
      </span>
      <div>
        <h2>{title}</h2>
        {children}
      </div>
    </aside>
  );
}

function EditorialCtaBlock({
  sourcePage,
  eyebrow,
  title,
  text,
  primary,
  secondary,
}: {
  sourcePage: string;
  eyebrow: string;
  title: string;
  text: string;
  primary: {
    label: string;
    href: string;
    ctaId: string;
    target: string;
  };
  secondary?: {
    label: string;
    href: string;
    ctaId: string;
    target: string;
  };
}) {
  return (
    <aside className="editorial-cta">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        <p>{text}</p>
      </div>
      <div className="editorial-cta-actions">
        <a
          className="button button-primary"
          href={primary.href}
          onClick={() => trackEditorialCta(sourcePage, primary.ctaId, primary.target, primary.href)}
        >
          {primary.label}
        </a>
        {secondary && (
          <a
            className="button button-secondary"
            href={secondary.href}
            onClick={() => trackEditorialCta(sourcePage, secondary.ctaId, secondary.target, secondary.href)}
          >
            {secondary.label}
          </a>
        )}
      </div>
    </aside>
  );
}

function GuidesIndexPage() {
  const guideCards = [
    {
      eyebrow: "Buying guide",
      title: "Work Tote Buying Guide",
      text:
        "A product-focused checklist for judging structure, closure, organization, comfort, returns, and price before you choose a tote.",
      href: buyingGuidePath,
      ctaId: "guides_buying_guide",
    },
    {
      eyebrow: "Size guide",
      title: "13-Inch vs 15-Inch Laptop Fit",
      text:
        "A practical sizing guide for checking internal dimensions, opening width, laptop sleeves, protective cases, and daily carry space.",
      href: sizeGuidePath,
      ctaId: "guides_size_guide",
    },
    {
      eyebrow: "Commute checklist",
      title: "Work Tote Commute Checklist",
      text:
        "A route-first checklist for matching a tote to transit, walking distance, office access, weather, layers, and after-work plans.",
      href: commuteChecklistPath,
      ctaId: "guides_commute_checklist",
    },
    {
      eyebrow: "Materials guide",
      title: "Leather vs Nylon Work Totes",
      text:
        "A practical comparison of leather, nylon, and hybrid materials by polish, weight, weather tolerance, maintenance, and commute use.",
      href: materialsGuidePath,
      ctaId: "guides_materials_guide",
    },
    {
      eyebrow: "Editorial method",
      title: "How We Pick Work Totes",
      text:
        "The selection criteria behind our current work tote shortlist, including laptop fit, structure, organization, and low-logo polish.",
      href: howWePickPath,
      ctaId: "guides_how_we_pick",
    },
  ];

  return (
    <main>
      <section className="guides-hero shell">
        <div>
          <p className="eyebrow">Guides library</p>
          <h1>Practical office carry guides, not just product links.</h1>
        </div>
        <p>
          These editorial pages support the Work Tote Edit with more context: how to judge size, structure, commute
          fit, buying tradeoffs, and our selection method before clicking through to any merchant.
        </p>
      </section>

      <section className="guides-section shell" aria-labelledby="guides-title">
        <div className="section-heading split-heading">
          <div>
            <p className="eyebrow">Current work tote research</p>
            <h2 id="guides-title">Start with the question you are trying to answer.</h2>
          </div>
          <p>
            The goal is to help a reader make a better buying decision before they land on a merchant page. Each guide
            covers one decision layer and then points back to the current shortlist when comparison is useful.
          </p>
        </div>
        <div className="guide-card-grid">
          {guideCards.map((guide, index) => (
            <a
              className="guide-card"
              href={guide.href}
              key={guide.href}
              onClick={() => trackEditorialCta("guides", guide.ctaId, guide.title, guide.href)}
            >
              <span className="guide-card-index">{String(index + 1).padStart(2, "0")}</span>
              <p className="eyebrow">{guide.eyebrow}</p>
              <h3>{guide.title}</h3>
              <p>{guide.text}</p>
            </a>
          ))}
        </div>
      </section>

      <section className="guides-section guides-section-muted" aria-labelledby="guides-role-title">
        <div className="shell">
          <div className="section-heading split-heading">
            <div>
              <p className="eyebrow">Why this section exists</p>
              <h2 id="guides-role-title">A stronger guide library makes the site more useful and more credible.</h2>
            </div>
            <p>
              The product page is where comparison happens. The guides library is where we explain the reasoning behind
              those comparisons, so the site reads like an editorial resource instead of a thin shopping page.
            </p>
          </div>
          <div className="editorial-feature-grid">
            <article>
              <BookOpenCheck size={22} />
              <h2>For readers</h2>
              <p>
                It gives shoppers a way to answer practical questions before choosing a merchant: sizing, commute
                pressure, closure tradeoffs, and whether a tote actually fits the workday.
              </p>
            </article>
            <article>
              <SearchCheck size={22} />
              <h2>For long-term growth</h2>
              <p>
                It creates a clean place to add future editorial content without cluttering the main navigation or
                turning the site into a pile of unrelated pages.
              </p>
            </article>
          </div>
        </div>
      </section>
    </main>
  );
}

function HowWePickPage() {
  return (
    <main className="editorial-page">
      <EditorialHero
        eyebrow="Editorial method"
        title="How We Pick Work Totes"
        subtitle="Our work tote guide is built around practical office carry needs: laptop fit, structure, organization, commute comfort, and low-logo polish."
        image="/images/editorial/editorial-selection-criteria-desk.webp"
        imageAlt="Neutral office desk with work tote evaluation notes and carry essentials"
      />

      <section className="editorial-body shell">
        <div className="editorial-intro">
          <p>
            This guide is for readers who want a bag that feels appropriate in an office, holds daily work essentials,
            and avoids counterfeit, replica, or designer-dupe framing. We focus on transferable shopping needs rather
            than chasing a specific luxury logo.
          </p>
        </div>

        <div className="editorial-section-grid">
          <article>
            <h2>Our selection criteria</h2>
            <ul className="editorial-check-list">
              <li>
                <Check size={16} />
                <span>Structure that keeps the bag looking polished when it is set down.</span>
              </li>
              <li>
                <Check size={16} />
                <span>Laptop-friendly sizing with attention to sleeve, opening width, and real carry space.</span>
              </li>
              <li>
                <Check size={16} />
                <span>Interior organization for charger, keys, wallet, notebook, and small daily items.</span>
              </li>
              <li>
                <Check size={16} />
                <span>Commute comfort, including shoulder drop, weight, closure, and travel-friendly details.</span>
              </li>
              <li>
                <Check size={16} />
                <span>Low-logo visual polish that works with office clothing instead of competing with it.</span>
              </li>
            </ul>
          </article>
          <img
            className="editorial-inline-image"
            src="/images/editorial/editorial-laptop-fit-guide.webp"
            alt="Unbranded structured tote with laptop sleeve and office desk essentials"
          />
        </div>

        <EditorialCallout icon={<ShieldCheck size={20} />} title="What we do not recommend">
          <p>
            We do not recommend counterfeit goods, replica products, or pages that frame a product as a fake version of
            a protected designer item. The editorial focus is practical: shape, material, size, organization, and office
            readiness.
          </p>
        </EditorialCallout>

        <div className="editorial-feature-grid">
          <article>
            <BookOpenCheck size={22} />
            <h2>Editorial independence</h2>
            <p>
              Some merchant links may support the site. Products still need to fit our selection criteria, and
              affiliate availability does not guarantee placement.
            </p>
          </article>
          <article>
            <SearchCheck size={22} />
            <h2>How often we review products</h2>
            <p>
              Prices, colors, availability, and merchant policies can change. We review the guide periodically and
              expect readers to confirm final product details, shipping, and returns on the merchant site before buying.
            </p>
          </article>
        </div>

        <EditorialCtaBlock
          sourcePage="how_we_pick"
          eyebrow="Apply the method"
          title="Use these criteria against the current tote shortlist."
          text="If the selection method makes sense for your workday, compare the current picks by structure, laptop fit, commute details, and price before clicking through to a merchant."
          primary={{
            label: "View the picks",
            href: "/work-totes#picks",
            ctaId: "how_we_pick_view_picks",
            target: "work_totes_picks",
          }}
          secondary={{
            label: "Compare details",
            href: "/work-totes#compare",
            ctaId: "how_we_pick_compare",
            target: "work_totes_compare",
          }}
        />
      </section>
    </main>
  );
}

function BuyingGuidePage() {
  const guideItems = [
    {
      title: "Laptop fit is more than screen size",
      text:
        "A 13-inch or 15-inch label is only a starting point. Check internal dimensions, opening width, sleeve placement, and whether your laptop needs extra room for a protective case.",
      image: "/images/editorial/editorial-laptop-fit-guide.webp",
      alt: "Laptop placed inside an unbranded structured work tote",
    },
    {
      title: "Structure matters for office polish",
      text:
        "A tote does not need to be stiff, but it should hold a clean shape with daily contents inside. A structured base and reinforced panels help the bag read more professional.",
      image: "/images/editorial/editorial-structure-office-polish.webp",
      alt: "Structured work tote styled in a polished office setting",
    },
    {
      title: "Closure changes security and access",
      text:
        "Open-top totes are easy to reach into, zippers feel more secure, and magnetic closures sit in the middle. Treat closure as a product feature, not just a styling detail.",
      image: "/images/editorial/editorial-closure-types.webp",
      alt: "Unbranded tote closure details shown in a neutral editorial layout",
    },
    {
      title: "Interior layout should reduce rummaging",
      text:
        "Look for a layout that separates laptop, charger, wallet, keys, bottle, and small pouches without forcing every item into tiny pockets you will stop using.",
      image: "/images/editorial/editorial-interior-organization-flatlay.webp",
      alt: "Flat lay of tote interior organization with laptop, charger, bottle, and pouch",
    },
  ];

  return (
    <main className="editorial-page">
      <EditorialHero
        eyebrow="Buying guide"
        title="Work Tote Buying Guide"
        subtitle="A product-focused checklist for judging whether a work tote is worth buying before you compare colors, discounts, or brand appeal."
        image="/images/editorial/editorial-final-checklist.webp"
        imageAlt="Editorial checklist scene for choosing a professional work tote"
      />

      <section className="editorial-body shell">
        <div className="buying-guide-grid">
          {guideItems.map((item) => (
            <article className="buying-guide-card" key={item.title}>
              <img src={item.image} alt={item.alt} loading="lazy" />
              <div>
                <h2>{item.title}</h2>
                <p>{item.text}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="editorial-section-grid editorial-section-grid-reverse">
          <img
            className="editorial-inline-image"
            src="/images/editorial/editorial-commute-comfort.webp"
            alt="Professional work tote arranged for daily commute comfort"
          />
          <article>
            <h2>Comfort is still part of product quality</h2>
            <p>
              A beautiful tote can still be a poor buy if the straps are too short, the empty weight is high, or the
              shape pulls awkwardly once a laptop and charger are added. Treat strap drop, base structure, and empty
              weight as part of the product evaluation.
            </p>
            <p>
              The route-specific question comes later. First, decide whether the tote itself has the right dimensions,
              construction, organization, closure, and return terms to deserve a place on your shortlist.
            </p>
          </article>
        </div>

        <div className="price-band">
          <div>
            <p className="eyebrow">Price bands</p>
            <h2>What to expect at each budget</h2>
          </div>
          <div className="price-band-grid">
            <article>
              <strong>$100-$150</strong>
              <p>Expect simple materials, fewer compartments, and more tradeoffs around structure or closure.</p>
            </article>
            <article>
              <strong>$150-$250</strong>
              <p>This is often the strongest testing range for polished, practical work totes with usable details.</p>
            </article>
            <article>
              <strong>$300+</strong>
              <p>Look for better materials, cleaner construction, and fewer compromises, not just a higher price.</p>
            </article>
          </div>
        </div>

        <EditorialCallout icon={<ClipboardCheck size={20} />} title="Final checklist before you buy">
          <ul className="editorial-check-list compact">
            <li>
              <Check size={16} />
              <span>Confirm laptop dimensions against the merchant measurements.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Check closure type, return policy, shipping cost, and final price.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Review interior photos, not just the front-facing product image.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Compare material, construction, organization, and return policy before focusing on styling.</span>
            </li>
          </ul>
        </EditorialCallout>

        <EditorialCtaBlock
          sourcePage="buying_guide"
          eyebrow="Turn the checklist into a shortlist"
          title="Ready to compare work totes against these buying rules?"
          text="The current Work Tote Edit applies this product checklist across laptop fit, organization, closure, structure, comfort, and price bands."
          primary={{
            label: "View the picks",
            href: "/work-totes#picks",
            ctaId: "buying_guide_view_picks",
            target: "work_totes_picks",
          }}
          secondary={{
            label: "Check commute fit",
            href: commuteChecklistPath,
            ctaId: "buying_guide_commute_checklist",
            target: "commute_checklist",
          }}
        />
      </section>
    </main>
  );
}

function WorkToteSizeGuidePage() {
  return (
    <main className="editorial-page">
      <EditorialHero
        eyebrow="Size guide"
        title="Work Tote Size Guide: 13-Inch vs 15-Inch Laptop Fit"
        subtitle="A practical guide to checking laptop fit, sleeve dimensions, opening width, and daily carry space before you choose a work tote."
        image="/images/editorial/editorial-work-tote-size-guide.webp"
        imageAlt="Laptop placed inside an unbranded structured work tote"
      />

      <section className="editorial-body shell">
        <div className="editorial-intro">
          <p>
            Laptop fit is one of the most misleading parts of shopping for a work tote. A product name might say “fits
            a 15-inch laptop,” but the real answer depends on the laptop body, protective case, sleeve placement, top
            opening, and what else you carry next to it.
          </p>
        </div>

        <div className="editorial-section-grid">
          <article>
            <h2>Start with the actual laptop body, not the marketing label.</h2>
            <p>
              A 13-inch, 14-inch, or 15-inch label usually refers to screen size, not the outside dimensions of the
              laptop. Two laptops with the same screen size can have different widths, depths, and corner shapes.
            </p>
            <p>
              If you use a hard shell, padded case, or privacy cover, measure that full package. The tote needs to fit
              the laptop you carry, not the laptop listed on a spec sheet.
            </p>
          </article>
          <img
            className="editorial-inline-image"
            src="/images/editorial/editorial-interior-organization-flatlay.webp"
            alt="Flat lay of work tote essentials including laptop, charger, pouch, bottle, and notebook"
          />
        </div>

        <div className="size-guide-table-wrap" aria-labelledby="size-table-title">
          <div>
            <p className="eyebrow">Laptop fit reference</p>
            <h2 id="size-table-title">What each laptop size usually means for tote shopping</h2>
          </div>
          <div className="size-guide-table">
            <div className="size-guide-row size-guide-row-head">
              <span>Laptop label</span>
              <span>What to check</span>
              <span>Best tote signal</span>
            </div>
            <div className="size-guide-row">
              <span>13-inch</span>
              <span>Most work totes can fit it, but a case or sleeve can still make the opening feel tight.</span>
              <span>Internal sleeve, slim profile, easy daily carry.</span>
            </div>
            <div className="size-guide-row">
              <span>14-inch</span>
              <span>Often fits in totes labeled for 13-inch laptops, but only if the opening and sleeve are generous.</span>
              <span>Confirmed internal dimensions, not just “laptop-friendly.”</span>
            </div>
            <div className="size-guide-row">
              <span>15-inch</span>
              <span>Needs more attention to top opening, bag width, sleeve depth, and whether the laptop sits upright.</span>
              <span>Dedicated laptop section or merchant confirmation.</span>
            </div>
            <div className="size-guide-row">
              <span>16-inch</span>
              <span>Often pushes a tote into travel or large-work-bag territory and may reduce comfort.</span>
              <span>Large tote format, reinforced base, clear return policy.</span>
            </div>
          </div>
        </div>

        <div className="editorial-feature-grid">
          <article>
            <Ruler size={22} />
            <h2>Opening width matters</h2>
            <p>
              A laptop can technically fit inside a tote and still be annoying if the top opening is narrow. Check
              whether the laptop slides in smoothly without bending corners or catching on the zipper.
            </p>
          </article>
          <article>
            <Laptop size={22} />
            <h2>Sleeves reduce usable space</h2>
            <p>
              A padded sleeve is useful, but it can make the interior feel smaller. If the sleeve is tight, chargers,
              bottles, notebooks, and pouches may compete for the same space.
            </p>
          </article>
          <article>
            <BriefcaseBusiness size={22} />
            <h2>Consider how the tote stands</h2>
            <p>
              A larger laptop changes the weight distribution. A structured base helps the tote stay upright at a desk,
              in a car, or under a meeting room chair.
            </p>
          </article>
          <article>
            <ShieldCheck size={22} />
            <h2>Use the return policy as a safety net</h2>
            <p>
              If laptop fit is close, do not rely on guesswork. Confirm final dimensions on the merchant page and check
              return rules before buying.
            </p>
          </article>
        </div>

        <EditorialCallout icon={<ClipboardCheck size={20} />} title="Quick fit check before you buy">
          <ul className="editorial-check-list compact">
            <li>
              <Check size={16} />
              <span>Measure your laptop with its protective case, not just the device alone.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Compare width, height, and depth against the tote's internal dimensions.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Look for photos showing the actual sleeve or interior laptop placement.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Check whether the tote still has room for charger, pouch, wallet, bottle, and notebook.</span>
            </li>
            <li>
              <Check size={16} />
              <span>If the fit is close, favor merchants with clear returns instead of gambling on a tight sleeve.</span>
            </li>
          </ul>
        </EditorialCallout>

        <EditorialCtaBlock
          sourcePage="size_guide"
          eyebrow="Compare by size"
          title="Use laptop fit as the first filter, then compare the current shortlist."
          text="The Work Tote Edit includes laptop details and commute notes so you can narrow the field before opening merchant pages."
          primary={{
            label: "Compare work totes",
            href: "/work-totes#compare",
            ctaId: "size_guide_compare",
            target: "work_totes_compare",
          }}
          secondary={{
            label: "Read buying guide",
            href: buyingGuidePath,
            ctaId: "size_guide_buying_guide",
            target: "buying_guide",
          }}
        />
      </section>
    </main>
  );
}

function LeatherVsNylonGuidePage() {
  return (
    <main className="editorial-page">
      <EditorialHero
        eyebrow="Materials guide"
        title="Leather vs Nylon Work Totes: Which Material Fits Your Workday?"
        subtitle="A practical comparison of structure, weight, weather tolerance, maintenance, and office polish before you choose a work tote material."
        image="/images/editorial/editorial-leather-vs-nylon-work-totes.webp"
        imageAlt="Leather and nylon work totes arranged side by side in a warm office setting"
      />

      <section className="editorial-body shell">
        <div className="editorial-intro">
          <p>
            Material choice changes how a work tote feels every day. Leather can look more structured and office-ready,
            but it often adds weight and care requirements. Nylon is usually lighter and more forgiving in bad weather,
            but some styles can read too casual for a polished workplace.
          </p>
        </div>

        <div className="editorial-section-grid">
          <article>
            <h2>The real question is not which material is better.</h2>
            <p>
              The better question is which tradeoff fits your route, office setting, and load. A desk-to-car routine can
              support a heavier structured tote. A subway commute, rainy climate, or frequent travel day may make a
              lighter nylon or coated material feel more useful.
            </p>
            <p>
              For work totes, material should be judged together with shape, handle comfort, closure, base structure,
              and whether the bag still looks intentional when it is full.
            </p>
          </article>
          <img
            className="editorial-inline-image"
            src="/images/editorial/editorial-commute-comfort.webp"
            alt="Professional work tote arranged for evaluating commute comfort"
          />
        </div>

        <div className="price-band" aria-labelledby="materials-comparison-title">
          <div>
            <p className="eyebrow">Material comparison</p>
            <h2 id="materials-comparison-title">How each material usually behaves in real work use</h2>
          </div>
          <div className="price-band-grid">
            <article>
              <strong>Leather</strong>
              <p>
                Best when you want polish, structure, and a more elevated office look. Watch for empty weight, scuffing,
                rain tolerance, and whether the handles stay comfortable when the tote is full.
              </p>
            </article>
            <article>
              <strong>Nylon</strong>
              <p>
                Best when you want lighter carry, weather forgiveness, and travel flexibility. Check that the shape,
                hardware, and finish still feel office-appropriate instead of gym-bag casual.
              </p>
            </article>
            <article>
              <strong>Coated or hybrid</strong>
              <p>
                Best when you want a middle path: easier cleanup, some structure, and less precious daily use. Inspect
                seams, edge finishing, and whether the material creases or collapses.
              </p>
            </article>
          </div>
        </div>

        <div className="editorial-feature-grid">
          <article>
            <BriefcaseBusiness size={22} />
            <h2>Choose leather for structure and polish</h2>
            <p>
              Leather usually works best when the tote is part of a polished work uniform: blazers, client meetings,
              desk days, and low-logo outfits. It should hold its shape without becoming stiff or awkward.
            </p>
          </article>
          <article>
            <Luggage size={22} />
            <h2>Choose nylon for long routes</h2>
            <p>
              Nylon is easier to recommend when the commute is the hard part of the day. If you walk, travel, or carry
              gym and weather extras, a lighter tote can matter more than a more formal finish.
            </p>
          </article>
          <article>
            <ShieldCheck size={22} />
            <h2>Check care before you buy</h2>
            <p>
              A beautiful tote can become stressful if every rain shower or scuff feels like damage. Look for merchant
              care notes, material descriptions, and return rules before assuming a bag fits your routine.
            </p>
          </article>
          <article>
            <Grid2x2Check size={22} />
            <h2>Interior design still matters</h2>
            <p>
              Material does not solve organization. Laptop sleeve, bottle placement, pocket layout, lining quality, and
              closure often determine whether a tote works better than the material label alone suggests.
            </p>
          </article>
        </div>

        <EditorialCallout icon={<ClipboardCheck size={20} />} title="Material checklist before opening a merchant page">
          <ul className="editorial-check-list compact">
            <li>
              <Check size={16} />
              <span>Check empty weight before comparing size or price.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Look for real interior photos to see whether the material holds shape when packed.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Match the material to your route: rain, walking distance, travel days, and public transit.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Read care instructions and return policy if the material is expensive or delicate.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Decide whether your workplace needs a more formal finish or simply a clean, low-logo shape.</span>
            </li>
          </ul>
        </EditorialCallout>

        <EditorialCtaBlock
          sourcePage="materials_guide"
          eyebrow="Turn material tradeoffs into a shortlist"
          title="Compare current work tote picks by polish, commute comfort, and laptop fit."
          text="The Work Tote Edit combines material notes with structure, organization, closure, and price so you can choose for the whole workday."
          primary={{
            label: "View the picks",
            href: "/work-totes#picks",
            ctaId: "materials_guide_view_picks",
            target: "work_totes_picks",
          }}
          secondary={{
            label: "Check size guide",
            href: sizeGuidePath,
            ctaId: "materials_guide_size_guide",
            target: "size_guide",
          }}
        />
      </section>
    </main>
  );
}

function CommuteChecklistPage() {
  return (
    <main className="editorial-page">
      <EditorialHero
        eyebrow="Commute checklist"
        title="Work Tote Commute Checklist"
        subtitle="A route-first checklist for testing whether a work tote fits your actual morning, office day, weather, and after-work plans."
        image="/images/editorial/editorial-commute-comfort.webp"
        imageAlt="Professional work tote arranged for daily commute comfort"
      />

      <section className="editorial-body shell">
        <div className="editorial-intro">
          <p>
            A tote can look polished in a product photo and still feel wrong by 8:45 a.m. This checklist starts with
            the commute instead of the brand name: what you carry, how crowded your route gets, how much walking you do,
            and whether the bag needs to move from desk to meeting to dinner.
          </p>
        </div>

        <div className="editorial-feature-grid">
          <article>
            <Luggage size={22} />
            <h2>Pack your real day first</h2>
            <p>
              List the non-negotiables first: laptop, charger, wallet, keys, badge, bottle, notebook, pouch, umbrella,
              and lunch. This page is about simulating your route after the product passes the basic buying criteria.
            </p>
          </article>
          <article>
            <BriefcaseBusiness size={22} />
            <h2>Choose for route pressure</h2>
            <p>
              Crowded trains, long walks, elevators, security desks, and coffee stops all change what matters. A route
              with more friction usually needs better closure, faster access, and a bag that stays close to the body.
            </p>
          </article>
          <article>
            <Laptop size={22} />
            <h2>Test access speed</h2>
            <p>
              Imagine removing your laptop, badge, wallet, keys, and earbuds without putting the whole bag on the
              floor. A commute-friendly tote should make common actions easier, not just fit everything once.
            </p>
          </article>
          <article>
            <ShieldCheck size={22} />
            <h2>Plan for weather and layers</h2>
            <p>
              Rain, winter coats, light blazers, and full hands all change how a tote feels. Check shoulder drop,
              closure, material tolerance, and whether the bag stays comfortable when your outfit changes.
            </p>
          </article>
        </div>

        <div className="price-band">
          <div>
            <p className="eyebrow">Commute patterns</p>
            <h2>Different routes need different tradeoffs</h2>
          </div>
          <div className="price-band-grid">
            <article>
              <strong>Transit-heavy</strong>
              <p>Prioritize closure, shoulder comfort, slim profile, and quick-access pockets.</p>
            </article>
            <article>
              <strong>Drive-to-office</strong>
              <p>Prioritize structure, laptop protection, desk polish, and easy loading.</p>
            </article>
            <article>
              <strong>Travel days</strong>
              <p>Prioritize trolley sleeve, secure top, light empty weight, and flexible capacity.</p>
            </article>
          </div>
        </div>

        <EditorialCallout icon={<ClipboardCheck size={20} />} title="Five-minute pre-purchase check">
          <ul className="editorial-check-list compact">
            <li>
              <Check size={16} />
              <span>Pack your normal route load and imagine carrying it for the longest walk in your day.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Check whether keys, phone, badge, and wallet are reachable without unpacking the tote.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Decide whether the route needs zipper security, a stable shoulder drop, or weather-tolerant material.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Imagine wearing it over your bulkiest coat or blazer, not just a thin shirt.</span>
            </li>
            <li>
              <Check size={16} />
              <span>Choose the tradeoff you can live with daily: weight, capacity, closure, material, or price.</span>
            </li>
          </ul>
        </EditorialCallout>

        <EditorialCtaBlock
          sourcePage="commute_checklist"
          eyebrow="Compare by commute"
          title="Match the checklist to the current work tote picks."
          text="Use the comparison strip to move from commute requirements—closure, shoulder comfort, laptop fit, travel details—to a practical shortlist."
          primary={{
            label: "Compare details",
            href: "/work-totes#compare",
            ctaId: "commute_checklist_compare",
            target: "work_totes_compare",
          }}
          secondary={{
            label: "Read buying guide",
            href: buyingGuidePath,
            ctaId: "commute_checklist_buying_guide",
            target: "buying_guide",
          }}
        />
      </section>
    </main>
  );
}

function StickyCta() {
  const [hidden, setHidden] = useState(false);
  const [picksVisible, setPicksVisible] = useState(false);
  const [pastHero, setPastHero] = useState(false);

  useEffect(() => {
    const footer = document.getElementById("footer");
    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setHidden(entry.isIntersecting);
      },
      { threshold: 0.05 },
    );

    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const picks = document.getElementById("picks");
    if (!picks) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setPicksVisible(entry.isIntersecting);
      },
      { threshold: 0.03 },
    );

    observer.observe(picks);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setPastHero(window.scrollY > 560);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <a
      className={`sticky-cta ${hidden || picksVisible || !pastHero ? "sticky-cta-hidden" : ""}`}
      href="#picks"
      onClick={() => trackEvent("hero_cta_click", { target: "sticky_picks" })}
    >
      View picks
    </a>
  );
}

function useProductImpressions() {
  const seenIds = useRef(new Set<string>());

  useEffect(() => {
    const cards = document.querySelectorAll<HTMLElement>("[data-product-id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = (entry.target as HTMLElement).dataset.productId;
          const product = products.find((item) => item.id === id);
          if (!product || seenIds.current.has(product.id)) return;

          seenIds.current.add(product.id);
          trackEvent("product_impression", {
            product_id: product.id,
            merchant: product.trackingMerchant,
            position: product.rank,
            price_tier: product.priceTier,
          });
        });
      },
      { threshold: 0.45 },
    );

    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, []);
}

function normalizePath(pathname: string) {
  const path = pathname.replace(/\/+$/, "");
  return path || "/";
}

function getPageMeta(path: string) {
  if (path === "/") {
    return {
      title: "Office Carry Guide | Work Carry Buying Guides",
      description:
        "Office Carry Guide publishes practical buying guides for polished work carry, office bags, commutes, travel, and hybrid work.",
      pageType: "site_home",
    };
  }
  if (path === "/work-totes") {
    return {
      title: "Office Carry Guide | Polished Work Totes",
      description:
        "Compare polished, laptop-friendly work totes selected for office days, commutes, structure, organization, and quiet professional style.",
      pageType: "landing",
    };
  }
  if (path === "/guides") {
    return {
      title: "Guides | Office Carry Guide",
      description:
        "Browse Office Carry Guide editorial guides on work tote sizing, buying criteria, commute fit, and product comparison methods.",
      pageType: "guides",
    };
  }
  if (path === "/how-we-pick-work-totes") {
    return {
      title: "How We Pick Work Totes | Office Carry Guide",
      description:
        "See the editorial criteria Office Carry Guide uses to evaluate work totes, including structure, laptop fit, organization, commute comfort, and low-logo polish.",
      pageType: "editorial",
    };
  }
  if (path === "/guides/work-tote-buying-guide" || path === "/work-tote-buying-guide") {
    return {
      title: "Work Tote Buying Guide | Office Carry Guide",
      description:
        "A practical guide to choosing a work tote by laptop fit, structure, closure, organization, commute comfort, and price band.",
      pageType: "editorial",
    };
  }
  if (path === "/guides/work-tote-size-guide") {
    return {
      title: "Work Tote Size Guide | Office Carry Guide",
      description:
        "A practical guide to choosing a work tote by 13-inch, 14-inch, 15-inch, and 16-inch laptop fit, sleeve dimensions, and opening width.",
      pageType: "editorial",
    };
  }
  if (path === "/guides/leather-vs-nylon-work-totes") {
    return {
      title: "Leather vs Nylon Work Totes | Office Carry Guide",
      description:
        "Compare leather and nylon work totes by structure, weight, weather tolerance, office polish, maintenance, and commute tradeoffs.",
      pageType: "editorial",
    };
  }
  if (path === "/guides/work-tote-commute-checklist" || path === "/work-tote-commute-checklist") {
    return {
      title: "Work Tote Commute Checklist | Office Carry Guide",
      description:
        "A commute-first checklist for choosing a polished work tote that fits your daily load, route, laptop, weather, and office setting.",
      pageType: "editorial",
    };
  }
  if (path === "/about") {
    return {
      title: "About | Office Carry Guide",
      description: "Learn about Office Carry Guide, an independent editorial shopping guide for office carry research.",
      pageType: "about",
    };
  }
  if (path === "/contact") {
    return {
      title: "Contact | Office Carry Guide",
      description: "Contact Office Carry Guide for editorial, privacy, affiliate disclosure, or publisher partnership questions.",
      pageType: "contact",
    };
  }
  if (path === "/affiliate-disclosure") {
    return {
      title: "Affiliate Disclosure | Office Carry Guide",
      description: "Read how Office Carry Guide uses affiliate links and how merchant links may support the site.",
      pageType: "legal",
    };
  }
  if (path === "/privacy") {
    return {
      title: "Privacy Policy | Office Carry Guide",
      description: "Read how Office Carry Guide handles analytics, affiliate links, cookies, and visitor information.",
      pageType: "legal",
    };
  }
  return {
    title: "Page Not Found | Office Carry Guide",
    description: "The Office Carry Guide page you requested could not be found.",
    pageType: "not_found",
  };
}

function LegalPageShell({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <main className="legal-page shell">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p className="legal-updated">Last updated: {lastUpdated}</p>
      <div className="legal-content">{children}</div>
    </main>
  );
}

function AboutPage() {
  return (
    <LegalPageShell eyebrow="About" title="About Office Carry Guide">
      <p>
        Office Carry Guide is an independent editorial shopping guide for people choosing polished, practical carry for
        office days, commutes, hybrid work, and work travel. We start with a real use case first, then compare products
        by the details that affect daily carry.
      </p>
      <p>
        Our first guide focuses on work totes because that is a clear shopping problem: readers often need a bag that
        looks appropriate at work, carries a laptop comfortably, handles a commute, and does not rely on loud branding
        to feel polished.
      </p>
      <h2>What we cover</h2>
      <p>
        The site currently covers polished work totes and related office carry questions. Future guides may expand into
        adjacent needs such as commuter bags, work travel bags, laptop-friendly office bags, pouches, organizers, and
        other carry pieces when there is a clear reader problem worth researching.
      </p>
      <h2>Who we write for</h2>
      <p>
        We write primarily for US shoppers, office workers, hybrid workers, and commuters who want low-logo,
        work-appropriate carry that can handle laptops, chargers, personal items, and the friction of a real day.
      </p>
      <h2>How we evaluate products</h2>
      <p>
        We organize recommendations around structure, laptop fit, interior organization, closure, shoulder comfort,
        travel details, materials, visual polish, merchant information, and price sanity. We try to explain the tradeoff
        behind each pick instead of treating every bag as equally useful for every reader.
      </p>
      <h2>Editorial independence</h2>
      <p>
        Some merchant links may support the site. Affiliate availability does not guarantee placement, and a product can
        still be included or excluded based on editorial fit. Product pages, prices, availability, shipping, returns,
        and checkout are handled by the merchant, not by Office Carry Guide.
      </p>
      <h2>What we do not do</h2>
      <p>
        We do not operate as a cashback, coupon, loyalty, browser-extension, or paid-search arbitrage publisher. We do
        not bid on merchant trademarks, and we do not frame products as counterfeit, replica, or designer-dupe
        alternatives. Our focus is practical shopping guidance: use case, shape, materials, organization, comfort, and
        office readiness.
      </p>
      <h2>Publisher details</h2>
      <p>
        Office Carry Guide is operated by BigAI as an editorial content and publisher operations project. The current
        public site is hosted at carry.bigaiseo.com.
      </p>
      <h2>Contact</h2>
      <p>
        For editorial, partnership, privacy, or publisher questions, email{" "}
        <a
          href="mailto:hello@bigaiseo.com"
          onClick={() => trackMailto("about", "editorial_partnership", "bigaiseo.com")}
        >
          hello@bigaiseo.com
        </a>
        .
      </p>
      <EditorialCtaBlock
        sourcePage="about"
        eyebrow="Start with the current guide"
        title="See how this editorial approach works on the work tote shortlist."
        text="The Work Tote Edit is our first live vertical: a practical comparison of polished, laptop-friendly bags for office days and commutes."
        primary={{
          label: "Explore work totes",
          href: "/work-totes",
          ctaId: "about_explore_work_totes",
          target: "work_totes_landing",
        }}
      />
    </LegalPageShell>
  );
}

function ContactPage() {
  return (
    <LegalPageShell eyebrow="Contact" title="Contact Office Carry Guide">
      <p>
        For questions about Office Carry Guide, product coverage, affiliate disclosures, privacy, or publisher
        partnerships, contact us by email.
      </p>
      <div className="contact-card">
        <p>
          <strong>Editorial and partnership contact</strong>
          <br />
          <a
            href="mailto:hello@bigaiseo.com"
            onClick={() => trackMailto("contact", "editorial_partnership", "bigaiseo.com")}
          >
            hello@bigaiseo.com
          </a>
        </p>
        <p>
          <strong>Account contact</strong>
          <br />
          <a href="mailto:bigaiseo@gmail.com" onClick={() => trackMailto("contact", "account", "gmail.com")}>
            bigaiseo@gmail.com
          </a>
        </p>
      </div>
      <p>
        We aim to respond to relevant business and editorial messages within a reasonable time. We do not sell products
        directly and cannot handle merchant order, shipping, or return requests.
      </p>
    </LegalPageShell>
  );
}

function AffiliateDisclosurePage() {
  return (
    <LegalPageShell eyebrow="Disclosure" title="Affiliate Disclosure">
      <p>
        Office Carry Guide is an independent shopping guide. Some links on this website are affiliate links or sponsored
        links. If you click a link and make a purchase, we may earn a commission at no extra cost to you.
      </p>
      <p>
        Affiliate links help support the time spent researching, organizing, and maintaining our shopping guides. Our
        recommendations are based on editorial fit, product positioning, use case, price, and public product information.
      </p>
      <h2>How affiliate links affect our content</h2>
      <p>
        Affiliate relationships do not guarantee placement. We may include products that are not part of an affiliate
        program, and we may exclude products if they do not fit the guide.
      </p>
      <h2>Merchant websites</h2>
      <p>
        Product buttons open third-party merchant websites. Prices, availability, shipping, return policies, and product
        details can change. Always review the merchant page before purchasing.
      </p>
      <h2>Advertising and paid search</h2>
      <p>
        We do not operate as a cashback, coupon, loyalty, or paid-search arbitrage publisher. Any future paid promotion
        will be reviewed against merchant and affiliate network policies before launch.
      </p>
    </LegalPageShell>
  );
}

function PrivacyPage() {
  return (
    <LegalPageShell eyebrow="Privacy" title="Privacy Policy">
      <p>
        This Privacy Policy explains how Office Carry Guide collects and uses information when you visit
        carry.bigaiseo.com.
      </p>
      <h2>Information we collect</h2>
      <p>
        We do not require visitors to create an account or submit personal information to read our guide. If you contact
        us by email, we receive the information you choose to send.
      </p>
      <h2>Analytics</h2>
      <p>
        We use analytics tools such as Google Analytics, Google Tag Manager, and Umami to understand page views,
        navigation, scroll behavior, outbound clicks, and general site performance. These tools may use cookies or
        similar technologies.
      </p>
      <h2>Affiliate and outbound links</h2>
      <p>
        When you click a product or merchant link, you may leave our website and visit a third-party merchant. Affiliate
        networks and merchants may collect information according to their own privacy policies.
      </p>
      <h2>Cookies</h2>
      <p>
        Our site and analytics providers may use cookies or similar technologies to measure traffic and site usage. You
        can control cookies through your browser settings.
      </p>
      <h2>Data sharing</h2>
      <p>
        We do not sell visitor personal information. We may share limited usage data with analytics and affiliate
        service providers that help operate, measure, or monetize the website.
      </p>
      <h2>Contact</h2>
      <p>
        For privacy questions, email{" "}
        <a href="mailto:hello@bigaiseo.com" onClick={() => trackMailto("privacy", "privacy", "bigaiseo.com")}>
          hello@bigaiseo.com
        </a>
        .
      </p>
    </LegalPageShell>
  );
}

function RoutedPage() {
  const path = normalizePath(window.location.pathname);

  if (path === "/") return <HomePage />;

  if (path === "/work-totes") {
    return (
      <main>
        <Hero />
        <CriteriaSection />
        <ProductSection />
        <CompareSection />
        <MethodSection />
        <FaqSection />
      </main>
    );
  }
  if (path === "/guides") return <GuidesIndexPage />;
  if (path === "/how-we-pick-work-totes") return <HowWePickPage />;
  if (path === "/guides/work-tote-buying-guide" || path === "/work-tote-buying-guide") return <BuyingGuidePage />;
  if (path === "/guides/work-tote-size-guide") return <WorkToteSizeGuidePage />;
  if (path === "/guides/leather-vs-nylon-work-totes") return <LeatherVsNylonGuidePage />;
  if (path === "/guides/work-tote-commute-checklist" || path === "/work-tote-commute-checklist") {
    return <CommuteChecklistPage />;
  }
  if (path === "/about") return <AboutPage />;
  if (path === "/contact") return <ContactPage />;
  if (path === "/affiliate-disclosure") return <AffiliateDisclosurePage />;
  if (path === "/privacy") return <PrivacyPage />;

  return <NotFoundPage />;
}

export function App() {
  const noscriptGtmId = useMemo(() => import.meta.env.VITE_GTM_CONTAINER_ID || import.meta.env.GTM_CONTAINER_ID, []);
  const path = normalizePath(window.location.pathname);
  const pageMeta = useMemo(() => getPageMeta(path), [path]);
  const isLandingPage = path === "/work-totes";

  useEffect(() => {
    installAnalyticsScripts();
    document.title = pageMeta.title;

    const metaDescription = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = pageMeta.description;
    }

    trackEvent("page_view", { path: window.location.pathname, page_type: pageMeta.pageType });
  }, [pageMeta]);

  useProductImpressions();

  return (
    <>
      {noscriptGtmId && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${noscriptGtmId}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="Google Tag Manager"
          />
        </noscript>
      )}
      <AppHeader />
      <RoutedPage />
      <Footer />
      {isLandingPage && <StickyCta />}
    </>
  );
}
